import useSearch from 'api/hooks/useSearch';
import { getInParameter } from 'components/Dashboard/ParliamentLive/ParliamentaryLiveRemarksWidget';
import replaceInvertedQuotes from 'components/Dashboard/utilities/replaceInvertedQuotes';
import { useEffect, useRef, useState } from 'react';

const TRANSCRIPT_TYPE = 'RealtimeUtterance';
const TARGET_ROW_VIEWPORT_RATIO = 1 / 3;
const PROGRAMMATIC_SCROLL_GUARD_MS = 150;
const IDLE_SEARCH = { status: 'idle', term: '', hits: [], index: -1 };

const isRenderable = (body) => !body?.notShown && !!body?.text?.trim();

const buildTranscriptQuery = ({ query, eventId }) => ({
  query,
  type: TRANSCRIPT_TYPE,
  filters: [{ field: 'eventId', value: `${eventId}`, operator: 'STR_EQ' }],
  sort: { field: 'dateTime', order: 'ASC' },
  pageNumber: 1,
  pageSize: 10000,
  doNotSearchTitle: true,
});

// Highlighted rows get their `text` swapped inside eventBodies (no reload); clear or a new search swaps it back.
const useTranscriptSearch = ({ eventId, eventBodiesState, scrollBarRef, updatingContributionRef }) => {
  const { eventBodies, setEventBodies } = eventBodiesState;
  const { getSearchResults, getHighlightedContent } = useSearch();

  const [inputValue, setInputValue] = useState('');
  const [search, setSearch] = useState(IDLE_SEARCH);

  const eventBodiesRef = useRef(eventBodies);
  eventBodiesRef.current = eventBodies;
  // objectID → text before the search touched the row (search or topic highlight), put back on reset
  const originalTexts = useRef(new Map());
  const searchHighlightedIds = useRef(new Set());
  const controllerRef = useRef(null);

  useEffect(() => () => controllerRef.current?.abort('Transcript search unmounted.'), []);

  // Keeps the ref current between renders so restore → hide → highlight in one tick see each other's result.
  const updateBodies = (updater) => {
    eventBodiesRef.current = updater(eventBodiesRef.current);
    setEventBodies(updater);
  };

  const restoreOriginalTexts = () => {
    const originals = new Map(originalTexts.current);
    originalTexts.current.clear();
    searchHighlightedIds.current.clear();
    if (!originals.size) return;
    updateBodies((prev) =>
      prev.map((body) => (originals.has(body.objectID) ? { ...body, text: originals.get(body.objectID) } : body))
    );
  };

  // Topic remark navigation rewrites `text` with topic keywords; `body` is the raw text.
  const hideTopicHighlights = () => {
    const ids = new Set();
    eventBodiesRef.current.forEach((body) => {
      if (body.body == null || body.text === body.body) return;
      originalTexts.current.set(body.objectID, body.text);
      ids.add(body.objectID);
    });
    if (!ids.size) return;
    updateBodies((prev) => prev.map((body) => (ids.has(body.objectID) ? { ...body, text: body.body } : body)));
  };

  const resetSearch = () => {
    controllerRef.current?.abort('Transcript search replaced or cleared.');
    restoreOriginalTexts();
    setSearch(IDLE_SEARCH);
  };

  const scrollToHit = (hit) => {
    const row = document.getElementById(`event-${getInParameter({ itemUrl: hit.contentUrl })}`);
    const view = scrollBarRef.current?.view;
    if (!row || !view) return;
    const rowOffset = row.getBoundingClientRect().top - view.getBoundingClientRect().top;
    updatingContributionRef.current = true;
    view.scrollTop += rowOffset - view.clientHeight * TARGET_ROW_VIEWPORT_RATIO;
    setTimeout(() => {
      updatingContributionRef.current = false;
    }, PROGRAMMATIC_SCROLL_GUARD_MS);
  };

  const highlightRow = async ({ hit, term, signal }) => {
    const { objectID } = hit;
    const current = eventBodiesRef.current.find((body) => body.objectID === objectID);
    if (!current || searchHighlightedIds.current.has(objectID)) return;
    try {
      const data = await getHighlightedContent({
        objectId: objectID,
        objectType: hit.type ?? TRANSCRIPT_TYPE,
        query: term,
        andQueries: [],
        signal,
      });
      if (!data?.highlightedHtmlBody) return;
      if (!originalTexts.current.has(objectID)) originalTexts.current.set(objectID, current.text);
      searchHighlightedIds.current.add(objectID);
      updateBodies((prev) =>
        prev.map((body) => (body.objectID === objectID ? { ...body, text: data.highlightedHtmlBody } : body))
      );
    } catch (error) {}
  };

  const showResult = ({ hits, index, term }) => {
    setSearch({ status: 'done', term, hits, index });
    scrollToHit(hits[index]);
    highlightRow({ hit: hits[index], term, signal: controllerRef.current.signal });
  };

  const runSearch = async (term) => {
    resetSearch();
    hideTopicHighlights();
    const controller = new AbortController();
    controllerRef.current = controller;
    setSearch({ ...IDLE_SEARCH, status: 'loading', term });
    try {
      const data = await getSearchResults({
        ...buildTranscriptQuery({ query: term, eventId }),
        signal: controller.signal,
      });
      const renderedIds = new Set(eventBodiesRef.current.filter(isRenderable).map((body) => body.objectID));
      const hits = (data?.hits ?? []).filter((hit) => renderedIds.has(hit.objectID));
      if (hits.length) showResult({ hits, index: 0, term });
      else setSearch({ ...IDLE_SEARCH, status: 'done', term });
    } catch (error) {
      if (!controller.signal.aborted) setSearch(IDLE_SEARCH);
    }
  };

  const step = (delta) => {
    const { hits, index, term } = search;
    if (!hits.length) return;
    showResult({ hits, term, index: (index + delta + hits.length) % hits.length });
  };

  const submit = () => {
    const term = replaceInvertedQuotes(inputValue).trim();
    if (!term) return clear();
    if (term === search.term && search.status === 'done') return step(1);
    runSearch(term);
  };

  const changeInput = (value) => {
    setInputValue(value);
    if (value === '') resetSearch();
  };

  const clear = () => {
    setInputValue('');
    resetSearch();
  };

  return {
    inputValue,
    changeInput,
    submit,
    clear,
    next: () => step(1),
    prev: () => step(-1),
    status: search.status,
    isActive: search.status !== 'idle',
    total: search.hits.length,
    position: search.index + 1,
  };
};

export default useTranscriptSearch;
